import React, { useEffect, useMemo, useState } from 'react'
import { SliderPicker } from 'react-color'
import { DateTime } from 'luxon'
import {
    Button,
    CircularProgress,
    IconButton,
    Switch,
    TextField,
    Theme,
    Tooltip,
} from '@material-ui/core'

import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import InfoIcon from '@material-ui/icons/Info'
import SettingsIcon from '@material-ui/icons/Settings'

import { KeyboardDatePicker } from '@material-ui/pickers'

import { Container, NumberInput, Selector } from '../../../components'

import {
    CreateScheduleRequest,
    ScheduleDetailActionThunk,
    updateInspection,
    UpdateScheduleRequest,
} from '../../../store'
import { isHex } from '../../../helpers'
import {
    Company,
    Inspection,
    InspectionType,
    Schedule,
    User,
    WorkSpaceUser,
} from '../../../models'

import { useTimezone } from '../../../contexts/timezone'
import { useAppDispatch } from '../../../hooks'
import { CreateInspectionModal } from '../../Inspection/CreateInspectionModal'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    schedule: Schedule | null
    loading?: boolean
    color: string
    initialStartDate: DateTime
    apartment?: Company
    inspectionList?: Inspection[]
    userList: User[]
    inspectionTypeList: InspectionType[]
    setColor: (color: string) => void
    onClose: () => void
    createSchedule: (req: CreateScheduleRequest) => ScheduleDetailActionThunk
    updateSchedule: (req: UpdateScheduleRequest) => ScheduleDetailActionThunk
    onCreateProject?: () => void
    syncBedStatuses: (schedule: Schedule) => Promise<void>
    getUnitReport: (schedule: Schedule) => Promise<void>
    onAdvancedSettingsOpenChange?: (open: boolean) => void
}

export const ScheduleForm = (props: Props) => {
    const {
        theme,
        schedule,
        onClose,
        loading,
        createSchedule,
        updateSchedule,
        color,
        setColor,
        initialStartDate,
        apartment,
        inspectionList,
        onCreateProject,
        syncBedStatuses,
        getUnitReport,
        onAdvancedSettingsOpenChange,
    } = props

    const dispatch = useAppDispatch()

    const { currentTimezone } = useTimezone()
    const [doValidate, setDoValidate] = useState(false)
    const [name, setName] = useState(schedule?.name ?? '')
    const [scheduleId, setScheduleId] = useState(schedule?.schedule_id ?? '')
    const [baselineBudget, setBaselineBudget] = useState(0)
    const [archived, setArchived] = useState(false)
    const [autoSyncBedStatus, setAutoSyncBedStatus] = useState(true)
    const [applyMoveOutRange, setApplyMoveOutRange] = useState(false)
    const [moveOutOffset, setMoveOutOffset] = useState(30)
    const [
        advancedSettingsDrawerOpen,
        setAdvancedSettingsDrawerOpen,
    ] = useState(false)

    const [
        selectedInspection,
        setSelectedInspection,
    ] = useState<Inspection | null>(
        schedule?.inspection
            ? inspectionList?.find(
                  (inspection) => inspection.id === schedule.inspection,
              ) ?? null
            : null,
    )
    const [createInspectionModalOpen, setCreateInspectionModalOpen] = useState(
        false,
    )

    const beginingOfStartDate = initialStartDate.startOf('day')
    const endOfStartDate = initialStartDate.endOf('day')

    const [dateRange, setDateRange] = useState({
        startDate: beginingOfStartDate,
        endDate: endOfStartDate,
    })

    const [lastSyncDate, setLastSyncDate] = useState(
        schedule?.last_sync_date ?? null,
    )

    const inputStyle: React.CSSProperties = {
        marginBottom: theme.spacing(2),
    }

    const isValid = () => {
        const nameValid = name !== ''
        const scheduleIdValid = scheduleId !== ''
        const budgetValid = baselineBudget >= 0
        const colorValid = isHex(color)

        return nameValid && scheduleIdValid && budgetValid && colorValid
    }

    useEffect(() => {
        if (schedule) {
            setName(schedule.name)
            setScheduleId(schedule.schedule_id)
            setBaselineBudget(schedule.baseline_budget)
            setArchived(!schedule.active)

            const startDate = DateTime.fromISO(schedule.start_date).setZone(
                currentTimezone,
            )
            const endDate = DateTime.fromISO(schedule.end_date).setZone(
                currentTimezone,
            )

            setDateRange({
                startDate: startDate,
                endDate: endDate,
            })

            setApplyMoveOutRange(schedule.apply_move_out_range)
            setMoveOutOffset(schedule.move_out_offset)

            if (schedule.inspection) {
                setSelectedInspection(
                    inspectionList?.find(
                        (inspection) => inspection.id === schedule.inspection,
                    ) ?? null,
                )
            }
        } else {
            setName('')
            setScheduleId('')
            setBaselineBudget(0)
            setArchived(false)
            setDateRange({
                startDate: beginingOfStartDate,
                endDate: endOfStartDate,
            })
            setSelectedInspection(null)
        }
    }, [schedule])

    const validInspections = useMemo(
        () =>
            inspectionList?.filter(
                (inspection) =>
                    inspection.active &&
                    (inspection.schedule?.id === schedule?.id ||
                        !inspection.schedule),
            ),
        [inspectionList, schedule],
    )

    const autoSyncEligible = apartment?.entrata_integrated && !schedule

    const handleInspectionUpdate = (
        inspection: Inspection,
        schedule: Schedule,
    ) => {
        const updatedInspection: Inspection = {
            ...inspection,
            schedule: schedule,
        }

        dispatch(updateInspection(inspection.id, updatedInspection))
    }

    const handleInspectionCreated = (inspection: Inspection) => {
        setSelectedInspection(inspection)
    }

    const handleOpenAdvancedSettings = () => {
        setAdvancedSettingsDrawerOpen(true)
        onAdvancedSettingsOpenChange?.(true)
    }
    const handleCloseAdvancedSettings = () => {
        setAdvancedSettingsDrawerOpen(false)
        onAdvancedSettingsOpenChange?.(false)
    }

    const advancedPanel = (
        <Container
            style={{
                flexDirection: 'column',
                padding: theme.spacing(2),
                width: 340,
                flexShrink: 0,
                borderLeft: `1px solid ${theme.palette.grey[400]}`,
                backgroundColor: theme.palette.grey[50],
            }}
        >
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing(3),
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        color: theme.palette.grey[800],
                    }}
                >
                    Advanced Settings
                </span>
                <IconButton
                    size="small"
                    onClick={handleCloseAdvancedSettings}
                    aria-label="Close advanced settings"
                >
                    <CloseIcon style={{ color: theme.palette.grey[600] }} />
                </IconButton>
            </Container>

            {/* Project Color */}
            <Container
                style={{
                    flexDirection: 'column',
                    marginBottom: theme.spacing(3),
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        fontWeight: 300,
                        color: theme.palette.grey[600],
                        marginBottom: theme.spacing(1),
                    }}
                >
                    Project Color
                </span>
                <TextField
                    error={doValidate && !isHex(color)}
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    style={{ width: '100%', marginBottom: theme.spacing(2) }}
                    inputProps={{
                        style: {
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: 300,
                            color: theme.palette.grey[600],
                            textAlign: 'center',
                        },
                    }}
                />
                <SliderPicker
                    color={color}
                    onChange={(color) => {
                        setColor(color.hex)
                    }}
                />
            </Container>

            {/* Auto-Detect Move Outs */}
            {apartment?.entrata_integrated && (
                <Container
                    style={{
                        flexDirection: 'column',
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: theme.typography.fontWeightLight,
                                color: theme.palette.grey[600],
                            }}
                        >
                            Auto-Detect Move Outs
                        </span>
                        <Tooltip
                            title={
                                <div
                                    style={{
                                        fontSize: '14px',
                                        lineHeight: '1.4',
                                    }}
                                >
                                    <strong>Move Out Offset:</strong> Number of
                                    days before the schedule start date to check
                                    for ending leases.
                                    <br />
                                    <br />
                                    When syncing bed statuses, leases ending
                                    within this timeframe will be automatically
                                    marked as vacant. All other leases that are
                                    not renewals or transfers will be marked as
                                    DNT.
                                </div>
                            }
                        >
                            <InfoIcon
                                style={{
                                    fontSize: 20,
                                    marginLeft: theme.spacing(1),
                                    color: theme.palette.primary.main,
                                    cursor: 'help',
                                }}
                            />
                        </Tooltip>
                    </Container>
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Switch
                            checked={applyMoveOutRange}
                            color="primary"
                            onClick={() => {
                                setApplyMoveOutRange(!applyMoveOutRange)
                            }}
                        />
                        <TextField
                            value={moveOutOffset}
                            onChange={(e) =>
                                setMoveOutOffset(Number(e.target.value))
                            }
                            style={{ width: '100%' }}
                            type="number"
                            inputProps={{ min: 0 }}
                            label="Check Last X Days"
                            disabled={!applyMoveOutRange}
                            variant="outlined"
                            size="small"
                        />
                    </Container>
                </Container>
            )}

            {/* Run Initial Bed Status Sync */}
            {autoSyncEligible && (
                <Container
                    style={{
                        flexDirection: 'column',
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: theme.typography.fontWeightLight,
                                color: theme.palette.grey[600],
                            }}
                        >
                            Run Initial Bed Status Sync
                        </span>
                        <Tooltip
                            title={
                                'Are you sure you want to sync your bed statuses?\n\n' +
                                'This action can be destructive. If you have moved your leases out in Entrata ' +
                                'the bed statuses will be marked as vacant when renewal is what you expect.\n\n' +
                                'This action will download a unit report.'
                            }
                        >
                            <InfoIcon
                                style={{
                                    fontSize: 20,
                                    marginLeft: theme.spacing(1),
                                    color: theme.palette.primary.main,
                                    cursor: 'help',
                                }}
                            />
                        </Tooltip>
                    </Container>
                    <Switch
                        checked={autoSyncBedStatus}
                        color="primary"
                        onClick={() => {
                            setAutoSyncBedStatus(!autoSyncBedStatus)
                        }}
                    />
                </Container>
            )}

            {/* Archived */}
            {schedule && (
                <Container
                    style={{
                        flexDirection: 'column',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: 300,
                            color: theme.palette.grey[600],
                        }}
                    >
                        Archived
                    </span>
                    <Switch
                        checked={archived}
                        color="primary"
                        onClick={() => {
                            setArchived(!archived)
                        }}
                        disabled={schedule === null}
                    />
                </Container>
            )}
        </Container>
    )

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'row',
                width: '100%',
                minWidth: 0,
            }}
        >
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <Container
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: theme.spacing(1),
                        marginBottom: 0,
                    }}
                >
                    <Tooltip title="Advanced settings">
                        <IconButton
                            size="small"
                            onClick={handleOpenAdvancedSettings}
                            aria-label="Open advanced settings"
                            style={{
                                color: theme.palette.grey[600],
                            }}
                        >
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                </Container>

                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        padding: theme.spacing(1),
                        marginTop: 0,
                    }}
                >
                    <TextField
                        error={doValidate && name === ''}
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        label="Name"
                        style={inputStyle}
                    />

                    <TextField
                        error={doValidate && scheduleId === ''}
                        variant="outlined"
                        value={scheduleId}
                        onChange={(e) => setScheduleId(e.target.value)}
                        label="Project Id"
                        inputProps={{ maxLength: 8 }}
                        style={inputStyle}
                        disabled={schedule !== null}
                    />
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Selector
                            data={validInspections ?? []}
                            currentValue={selectedInspection?.id ?? ''}
                            onChange={(e) => {
                                const selectedId = e.target.value as number
                                const selected =
                                    validInspections?.find(
                                        (inspection) =>
                                            inspection.id === selectedId,
                                    ) ?? null
                                setSelectedInspection(selected)
                            }}
                            getDisplayString={(inspection) => inspection.name}
                            label="Inspection"
                            customStyle={{
                                formControl: {
                                    flex: 1,
                                },
                            }}
                        />

                        <Tooltip title="Create New Inspection">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    setCreateInspectionModalOpen(true)
                                }
                                style={{
                                    marginLeft: theme.spacing(1),
                                    padding: 4,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Container>

                    <NumberInput
                        error={doValidate && baselineBudget < 0}
                        prefix={'$'}
                        variant="outlined"
                        label="Set Budget"
                        value={baselineBudget}
                        onChange={(e) =>
                            setBaselineBudget(Number(e.target.value))
                        }
                        style={inputStyle}
                    />

                    <KeyboardDatePicker
                        error={dateRange.startDate === null}
                        value={dateRange.startDate.setZone(undefined, {
                            keepLocalTime: true,
                        })}
                        onChange={(date) => {
                            if (date === null) {
                                return
                            } else {
                                const newStartDate = DateTime.fromJSDate(date)
                                    .setZone(currentTimezone, {
                                        keepLocalTime: true,
                                    })
                                    .startOf('day')
                                const newEndDate =
                                    newStartDate > dateRange.endDate
                                        ? newStartDate.endOf('day')
                                        : dateRange.endDate
                                setDateRange({
                                    startDate: newStartDate,
                                    endDate: newEndDate,
                                })
                                if (
                                    apartment?.entrata_integrated &&
                                    !lastSyncDate
                                ) {
                                    setLastSyncDate(
                                        newStartDate.minus({ days: 1 }).toISO(),
                                    )
                                }
                            }
                        }}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="Start Date"
                        style={inputStyle}
                    />

                    <KeyboardDatePicker
                        error={dateRange.endDate === null}
                        value={dateRange.endDate.setZone(undefined, {
                            keepLocalTime: true,
                        })}
                        onChange={(date) => {
                            if (date === null) {
                                return
                            } else {
                                const newEndDate = DateTime.fromJSDate(date)
                                    .setZone(currentTimezone, {
                                        keepLocalTime: true,
                                    })
                                    .endOf('day')
                                console.log(
                                    newEndDate.toFormat('MM/dd/yyyy hh:mm a z'),
                                )
                                setDateRange({
                                    startDate: dateRange.startDate,
                                    endDate: newEndDate,
                                })
                            }
                        }}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="End Date"
                        style={inputStyle}
                    />
                    {apartment?.entrata_integrated && (
                        <KeyboardDatePicker
                            value={
                                lastSyncDate
                                    ? DateTime.fromISO(lastSyncDate).setZone(
                                          undefined,
                                          {
                                              keepLocalTime: true,
                                          },
                                      )
                                    : null
                            }
                            onChange={(date) => {
                                if (date === null) {
                                    setLastSyncDate(null)
                                } else {
                                    const newLastSyncDate = DateTime.fromJSDate(
                                        date,
                                    )
                                        .setZone(currentTimezone, {
                                            keepLocalTime: true,
                                        })
                                        .toISO()
                                    setLastSyncDate(newLastSyncDate)
                                }
                            }}
                            format="MM/dd/yyyy"
                            inputVariant="outlined"
                            label="Last Bed Status Sync Date"
                            style={inputStyle}
                            clearable
                        />
                    )}
                </Container>

                {/* Footer */}

                <Container
                    style={{
                        padding: theme.spacing(1),
                        borderTop: `1px solid ${theme.palette.grey[400]}`,
                    }}
                >
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(1, 0, 1, 2),
                            backgroundColor: theme.palette.secondary.dark,
                            color: 'white',
                            textTransform: 'none',
                            cursor: 'pointer',
                            width: 75,
                        }}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <div style={{ flex: 1 }} />

                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Button
                            variant="contained"
                            style={{
                                margin: theme.spacing(1, 0, 1, 2),
                                backgroundColor: theme.palette.primary.dark,
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                                minWidth: 75,
                                paddingLeft: theme.spacing(2),
                                paddingRight: theme.spacing(2),
                            }}
                            onClick={() => {
                                setDoValidate(true)

                                if (!isValid()) {
                                    return
                                }

                                if (schedule) {
                                    const isDraftConfirm =
                                        schedule.review_status === 'DRAFT'

                                    updateSchedule({
                                        scheduleId: schedule.id,
                                        body: {
                                            name: name,
                                            // Date-only: backend applies property-local day bounds.
                                            start_date:
                                                dateRange.startDate.toISODate() ??
                                                undefined,
                                            end_date:
                                                dateRange.endDate.toISODate() ??
                                                undefined,
                                            color: color,
                                            active: !archived,
                                            baseline_budget:
                                                baselineBudget ?? 0,
                                            inspection: selectedInspection?.id,
                                            last_sync_date:
                                                lastSyncDate ?? undefined,
                                            apply_move_out_range: applyMoveOutRange,
                                            move_out_offset: moveOutOffset,
                                            ...(isDraftConfirm && {
                                                review_status: 'CONFIRMED',
                                            }),
                                        },
                                    }).then((response) => {
                                        if (selectedInspection) {
                                            handleInspectionUpdate(
                                                selectedInspection,
                                                response.data,
                                            )
                                        }
                                        onClose()
                                    })
                                } else {
                                    createSchedule({
                                        body: {
                                            name: name,
                                            // Date-only: backend applies property-local day bounds.
                                            start_date:
                                                dateRange.startDate.toISODate() ??
                                                beginingOfStartDate.toISODate() ??
                                                DateTime.now().toISODate() ??
                                                '',
                                            end_date:
                                                dateRange.endDate.toISODate() ??
                                                endOfStartDate.toISODate() ??
                                                DateTime.now().toISODate() ??
                                                '',
                                            schedule_id: scheduleId,
                                            color: color,
                                            baseline_budget:
                                                baselineBudget ?? 0,
                                            apartment_id: apartment?.id,
                                            inspection: selectedInspection?.id,
                                            last_sync_date:
                                                lastSyncDate ?? undefined,
                                            apply_move_out_range: applyMoveOutRange,
                                            move_out_offset: moveOutOffset,
                                        },
                                    }).then((response) => {
                                        if (selectedInspection) {
                                            handleInspectionUpdate(
                                                selectedInspection,
                                                response.data,
                                            )
                                        }
                                        const schedule: Schedule = response.data
                                        if (
                                            autoSyncBedStatus &&
                                            autoSyncEligible
                                        ) {
                                            Promise.all([
                                                getUnitReport(schedule),
                                                syncBedStatuses(schedule),
                                            ]).then(() => {
                                                onCreateProject?.()
                                                onClose()
                                            })
                                        } else {
                                            onCreateProject?.()
                                            onClose()
                                        }
                                    })
                                }
                            }}
                        >
                            {schedule?.review_status === 'DRAFT'
                                ? 'Confirm Project'
                                : 'Save'}
                        </Button>
                    )}
                </Container>
            </Container>
            {advancedSettingsDrawerOpen && advancedPanel}
            <CreateInspectionModal
                open={createInspectionModalOpen}
                inspectionTypeList={props.inspectionTypeList}
                userList={props.userList}
                onClose={() => setCreateInspectionModalOpen(false)}
                onInspectionCreated={handleInspectionCreated}
            />
        </Container>
    )
}
